import { createRef, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { t } from 'i18next'

import DropDownMenu from 'src/components/shared/DropDownMenu/DropDownMenu'
import EnhancedTable from 'src/components/shared/EnhancedTable/EnhancedTable'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import {
  EditLxIcon,
  DeleteIcon,
  DefendantIcon,
  WarningFillIcon
} from 'src/components/shared/ui/Icons'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import LexnetDialog from 'src/components/shared/ui/LexnetDialog'
import { SwitchField } from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import LexnetIconButton from 'src/components/shared/ui/form/fields/LexnetIconButton'
import IntervinienteView from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/Interviniente/IntervinienteView'
import {
  checkTipoPersonaFisica,
  personaFisica
} from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/types'
import { useAlertContext } from 'src/contexts/AlertContext'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import useNewModal from 'src/hooks/useModal'
import { Orden } from 'src/interfaces/Catalog'
import { Direccion, Interviniente } from 'src/interfaces/EscritosLegales'
import { Contact } from 'src/interfaces/lexon'
import { theme } from 'src/providers/ThemeProvider'
import { getIntervenerAddressData } from 'src/services/IntervenerService'
import ContactsSelector, {
  ContactsSelectorRefType
} from '../LexonContacts/components/ContactsSelector'
import { intervinientesStyles as styles } from './IntervinientesStyles'
import RepresentantesDialog from './components/RepresentantesDialog/RepresentantesDialog'

type SelectedRow = {
  index: number
  item: Interviniente | null
}

type IntervinientesPropsType = {
  onNewInterviniente?: (data: Interviniente | null) => void
  onEditInterviniente?: (data: Interviniente) => void
}

export default function Intervinientes({
  onNewInterviniente = () => null,
  onEditInterviniente = () => null
}: IntervinientesPropsType): JSX.Element {
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )
  const [intervinientes, intervinientesFieldMeta] = useField(FormField.INTERVINIENTES)
  const [intervinientesFromLexonValidated, setIntervinientesFromLexonValidated] = useState(false)
  const { isOpened, close, open } = useNewModal()
  const { setFieldValue } = useFormikContext()
  const [dataTable, setDataTable] = useState<Interviniente[]>([])
  const [selectedRow, setSelectedRow] = useState<SelectedRow>({ index: -1 } as SelectedRow)
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const { addAlert } = useAlertContext()
  const [requiredTable, setRequiredTable] = useState<boolean>(false)
  const { tipoMensaje } = useTipoMensajeContext()
  const refContactsSelector = createRef<ContactsSelectorRefType>()

  const [formData, setFormData] = useState<Interviniente | null>(null)

  async function contactData(item: Contact | null) {
    if (!item) {
      setFormData(null)
      open()
      return
    }
    const {
      address = '',
      block,
      floor = '',
      number = '',
      km = '',
      letter = '',
      stair = '',
      postalCode = '',
      country = {},
      typeRoad = '',
      town = '',
      idProvince = ''
    } = item

    const intervenerAddressData = await getIntervenerAddressData(
      country?.description as string,
      typeRoad as string,
      town as string,
      idProvince as string
    )

    const direccion: Direccion | null = {
      sinNumero: false,
      numero: number || '',
      bloque: block,
      escalera: stair || '',
      km: km || '',
      piso: floor || '',
      letra: letter || '',
      pais: intervenerAddressData.pais || null,
      nombreVia: address || '',
      tipoVia: intervenerAddressData.tipoVia || null,
      provincia: intervenerAddressData.provincia || null,
      municipio: intervenerAddressData.municipio || null,
      codigoPostal: postalCode || '',
      ciudadLocalidad: ''
    }

    const {
      dateBirth,
      dni = '',
      name = '',
      firstSurname = '',
      secondSurname = '',
      motherName = '',
      fatherName = '',
      nationality = null,
      accountNumber = ''
      // placeOfBirth = null
    } = item

    const data: Interviniente = {
      [FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA]: accountNumber,
      [FormField.INTERVINIENTE_CUENTA_BANCARIA_NIF]: accountNumber ? dni : '',
      [FormField.INTERVINIENTE_CUENTA_BANCARIA_TITULAR]: accountNumber
        ? `${name} ${firstSurname}  ${secondSurname || ''}`
        : '',
      [FormField.INTERVINIENTE_FECHA_NACIMIENTO]: dateBirth,
      [FormField.INTERVINIENTE_IDENTIFICACION]: dni,
      [FormField.INTERVINIENTE_IDENTIFICACION_TIPO]: null,
      [FormField.INTERVINIENTE_INTERVENCION_TIPO]: null,
      [FormField.INTERVINIENTE_LUGAR_NACIMIENTO]: '',
      [FormField.INTERVINIENTE_NACIONALIDAD]: nationality,
      [FormField.INTERVINIENTE_NOMBRE]: name,
      [FormField.INTERVINIENTE_NOMBRE_MADRE]: motherName,
      [FormField.INTERVINIENTE_NOMBRE_PADRE]: fatherName,
      [FormField.INTERVINIENTE_PAIS_NACIMIENTO]: nationality,
      [FormField.INTERVINIENTE_PRIMER_APELLIDO]: firstSurname,
      [FormField.INTERVINIENTE_SEGUNDO_APELLIDO]: secondSurname,
      [FormField.INTERVINIENTE_SEXO]: null,
      [FormField.INTERVINIENTE_PERSONA_TIPO]: personaFisica,
      [FormField.INTERVINIENTE_REPRESENTANTES_LEGALES]: [],
      [FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES]: [],
      [FormField.INTERVINIENTE_DIRECCION]: direccion,
      [FormField.INTERVINIENTE_ADD_PROFESIONAL_REPRESENTANTE_PROCESAL]: false
    }

    onNewInterviniente(data)
  }

  function editRow(item: Interviniente, index: number) {
    onEditInterviniente(item)
  }

  function deleteRow(_item: Interviniente, index: number) {
    if (index !== -1) {
      const newData = [...dataTable.slice(0, index), ...dataTable.slice(index + 1)]
      setDataTable(newData)
      setFieldValue(FormField.INTERVINIENTES, newData)
    }
  }

  function openRepresentanteDialog(item: Interviniente, index: number) {
    setSelectedRow({ index, item })
    setIsOpenDialog(true)
  }

  const columns = [
    {
      Header: 'Tipo de intervención',
      accessor: (item: Interviniente) => {
        const tipoIntervencion = item.intervencionTipo?.descripcion || ''
        return item.validated ? (
          item.intervencionTipo?.descripcion
        ) : (
          <Box display="flex" flexDirection="row">
            <WarningFillIcon htmlColor={theme.colors.saffron200} sx={styles.iconWarning} />
            <Box flex="1 1 auto" alignSelf="center">
              {tipoIntervencion}
            </Box>
          </Box>
        )
      }
    },
    {
      Header: 'Interviniente',
      accessor: (item: Interviniente) => {
        return item && item.personaTipo && checkTipoPersonaFisica(item.personaTipo)
          ? item.nombre
          : `${item.primerApellido}, ${item.nombre}`
      }
    },
    {
      Header: 'Nº de identificación',
      accessor: 'identificacion'
    },
    {
      Header: 'Representación',
      accessor: (item: Interviniente, index: number) => {
        if (item.representantesLegales.length > 0 || item.representantesProcesales.length > 0) {
          return (
            <Box sx={{ marginLeft: '30px' }}>
              <LexnetIconButton
                aria-label={t('tooltips.representantes')}
                title={t('tooltips.representantes')}
                handleClick={() => openRepresentanteDialog(item, index)}
              >
                <DefendantIcon htmlColor={theme.corporate.primaryColor} sx={{ fontSize: '21px' }} />
              </LexnetIconButton>
            </Box>
          )
        }
        return <></>
      }
    },
    {
      Header: ' ',
      accessor: (item: Interviniente, index: number) => (
        <Box className="cellActions">
          <LexnetIconButton
            handleClick={() => editRow(item, index)}
            aria-label="Editar"
            title={t('tooltips.edit')}
            disabled={!orden.value}
          >
            <EditLxIcon />
          </LexnetIconButton>
          <LexnetIconButton
            handleClick={() =>
              addAlert({
                message: t('dialog.message.remove_interviniente'),
                type: AlertType.INFO,
                action: async () => {
                  await deleteRow(item, index)
                  addAlert({
                    message: t('dialog.message.register_deleted_success'),
                    type: AlertType.SUCCESS
                  })
                }
              })
            }
            aria-label={t('tooltips.remove')}
            title={t('tooltips.remove')}
          >
            <DeleteIcon />
          </LexnetIconButton>
        </Box>
      ),
      width: 100
    }
  ]

  function closeDialog() {
    setSelectedRow({ index: -1, item: null })
    setFormData(null)
    close()
  }

  useEffect(() => {
    if (intervinientes.value.length) {
      const allValidated = intervinientes.value.every(int => int.validated)

      if (intervinientesFromLexonValidated !== allValidated) {
        setIntervinientesFromLexonValidated(allValidated)
      }
    }

    setDataTable(intervinientes.value)

    if (
      tipoMensaje !== TipoMensaje.ESCRITO &&
      (!intervinientes.value || !intervinientes.value.length)
    ) {
      setRequiredTable(true)
    } else {
      setRequiredTable(false)
    }

    // eslint-disable-next-line
  }, [intervinientes, tipoMensaje])

  useEffect(() => {
    setFieldValue(FormField.INTERVINIENTES_FROM_LEXON_VALIDATED, intervinientesFromLexonValidated)
  }, [intervinientesFromLexonValidated, setFieldValue])

  return (
    <FieldsetGroup legend={t('form.legend.intervinientes')}>
      {intervinientes.value.length > 0 && !intervinientesFromLexonValidated && (
        <Box sx={styles.infoBar}>
          <InfoBar
            message={t('form.validation.warning_intervinientes')}
            type={AlertType.WARNING}
            showCloseIcon={false}
          />
        </Box>
      )}

      <Box id={FormField.INTERVINIENTES} sx={styles.wrapper}>
        <Box sx={styles.actions} display="flex" justifyContent="flex-end" alignItems="center">
          <DropDownMenu
            id="intervinientes"
            title={t('form.button.add')}
            items={[
              {
                title: t('form.button.addNew'),
                onClick: () => {
                  setSelectedRow({ index: -1, item: null })
                  setFormData(null)
                  onNewInterviniente(null)
                },
                disabled: !orden.value
              },
              {
                title: t('form.button.addFromLexon'),
                onClick: () => {
                  refContactsSelector?.current?.open()
                },
                disabled: !orden.value
              }
            ]}
          />
        </Box>
        <EnhancedTable
          variation="form"
          columns={columns}
          data={dataTable && Object.keys(dataTable).length > 0 ? dataTable : []}
          toolbar={false}
          noRegistersMessage={t('components.enhanced_table.no_registers.intervinientes')}
          noRegistersMessageRequired={requiredTable}
          required={false}
        />
      </Box>
      {intervinientesFieldMeta.error && (
        <Box component="span" sx={styles.requiredField}>
          {intervinientesFieldMeta.error}
        </Box>
      )}
      <LexnetDialog
        title={
          selectedRow.item
            ? t('form.dialog.title.editar_interviniente')
            : t('form.dialog.title.nuevo_interviniente')
        }
        isOpen={isOpened}
        close={closeDialog}
        dialogContentStyle={{ marginBottom: '50px' }}
      >
        <IntervinienteView closeDialog={closeDialog} formValues={formData ?? selectedRow.item} />
      </LexnetDialog>
      <ContactsSelector ref={refContactsSelector} contactData={contactData} />
      <RepresentantesDialog
        openDialog={isOpenDialog}
        interviniente={selectedRow.item}
        close={() => setIsOpenDialog(false)}
      />
      {tipoMensaje !== TipoMensaje.PERSONACION && (
        <SwitchField
          id={FormField.INTERVINIENTE_INFORMAR_FISCALIA}
          label={t('form.field.informar_fiscalia_interviniente')}
          sx={styles.switch}
        />
      )}
    </FieldsetGroup>
  )
}
